import React from 'react';
import { Play, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.12, delayChildren: 0.1 }
    }
  };
  const item = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050505] grain-overlay"
      data-testid="hero-section"
    >
      {/* Full-screen looping background video */}
      <div className="hero-video-bg" aria-hidden="true" data-testid="hero-video-bg">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video-el"
        >
          <source src={`${process.env.PUBLIC_URL}/hero-reel.mp4`} type="video/mp4" />
        </video>
      </div>

      {/* Dark overlays for text readability */}
      <div className="absolute inset-0 z-[1] bg-black/35"></div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/55 via-black/15 to-[#050505]"></div>

      {/* Radial gold glow accent */}
      <div className="absolute inset-0 z-[1] gold-radial-glow"></div>

      {/* Content */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-32 text-center"
      >
        <motion.div variants={item}>
          <img
            src="https://customer-assets.emergentagent.com/job_clipcraft-270/artifacts/c5t7ui84_GTMC_Logo-removebg-preview.png"
            alt="Go-To Media Collective"
            className="h-44 sm:h-64 lg:h-80 w-auto mx-auto mb-0"
          />
        </motion.div>

        <motion.p variants={item} className="overline -mt-6 sm:-mt-10 lg:-mt-12 mb-6">
          Video Editing &amp; Media Production
        </motion.p>

        <motion.h1
          variants={item}
          className="font-display text-5xl sm:text-6xl lg:text-8xl font-semibold tracking-tight leading-[1.02] mb-8"
        >
          <span className="text-white">Turning Local Business to</span>
          <br />
          <span className="gold-gradient text-shadow-gold italic">Local Celebrities</span>
        </motion.h1>

        <motion.p variants={item} className="text-lg sm:text-xl text-[#a0a0a0] mb-12 max-w-2xl mx-auto leading-relaxed">
          Professional video editing and media production that transforms raw footage into
          scroll-stopping content — delivered fast, every time.
        </motion.p>

        <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => scrollToSection('services')}
            className="cta-button"
            data-testid="hero-view-packages-btn"
          >
            View Packages
            <ChevronRight size={20} />
          </button>
          <button
            onClick={() => scrollToSection('portfolio')}
            className="cta-button-outline"
            data-testid="hero-watch-samples-btn"
          >
            <Play size={20} />
            Watch Samples
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg
          width="36"
          height="36"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[#d4af37]"
        >
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
